/*
Copyright (C) 2026  Integrated Tech9labs Pvt. Ltd.
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

/**
 * DR failovers waiting for a decision (DR Phase 11), shown in Change control above the cut-over requests. One
 * approval covers a whole run: every VM of the plan, wave by wave. Someone other than the requester decides.
 * Nothing is shown when nothing waits (or when disaster recovery is off).
 */

import * as React from "react";
import styled from "styled-components";
import { Check, ShieldAlert, X } from "lucide-react";

import { RUN_OPERATION_NAMES, drErrorMessage } from "@src/components/modules/DrModule/drUtils";
import { ActionButton } from "@src/components/ui/DetailsKit";
import { Muted, T9, formatDateTime, timeAgo } from "@src/components/ui/Lists/ListTable";
import { Notice, OpsInput } from "@src/components/ui/OpsDialog";
import drSource from "@src/sources/DrSource";

import type { DrApproval } from "@src/@types/Dr";

const POLL_MS = 15000;

const Panel = styled.section`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 12px 16px 0 16px;
  padding: 12px 14px;
  border: 1px solid var(--t9-accent-500);
  background: var(--t9-info-50);
  border-radius: 8px;
`;
const Title = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  color: ${T9.slate900};
`;
const Row = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 6px 12px;
  align-items: center;
  padding: 8px 0;
  border-top: 1px solid var(--t9-info-200);
  font-size: 13px;
  color: ${T9.slate700};
`;
const Controls = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  justify-content: flex-end;
`;

type Props = {
  /** The logged-in user's ID: their own requests wait for someone else. */
  userId?: string | null;
};

const DrApprovals = (props: Props) => {
  const [approvals, setApprovals] = React.useState<DrApproval[]>([]);
  const [comments, setComments] = React.useState<{ [id: string]: string }>({});
  const [busy, setBusy] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  const load = React.useCallback(async () => {
    try {
      setApprovals(await drSource.approvals(true));
    } catch {
      setApprovals([]); // disaster recovery off, or not allowed to read it: nothing to show
    }
  }, []);
  React.useEffect(() => {
    load();
    const timer = setInterval(load, POLL_MS);
    return () => clearInterval(timer);
  }, [load]);

  const decide = async (approval: DrApproval, action: "approve" | "reject" | "review") => {
    setBusy(approval.id);
    setError(null);
    try {
      await drSource.decideApproval(approval.id, action, (comments[approval.id] || "").trim() || undefined);
      await load();
    } catch (err) {
      setError(drErrorMessage(err, "SwiftIQ refused this decision."));
    } finally {
      setBusy(null);
    }
  };

  if (!approvals.length) return null;
  return (
    <Panel data-test-id="dr-approvals">
      <Title>
        <ShieldAlert size={16} color="var(--t9-info-700)" /> Disaster recovery runs waiting for approval or review ({approvals.length})
      </Title>
      <Muted>
        One approval starts the whole run: every VM of the plan, wave by wave. Production VMs are shut down for their
        final sync. Emergency failovers start at once (pre-approved) and are reviewed afterwards by someone else.
      </Muted>
      {error ? <Notice tone="danger">{error}</Notice> : null}
      {approvals.map(a => {
        const run = a.run;
        const mine = Boolean(props.userId) && a.requested_by === props.userId;
        const count = run?.workload_ids.length || 0;
        const emergency = a.decision === "emergency";
        const comment = (comments[a.id] || "").trim();
        return (
          <Row key={a.id} data-test-id={`dr-approval-${a.id}`}>
            <div>
              <strong>{RUN_OPERATION_NAMES[run?.operation || ""] || run?.operation || "DR run"}</strong>
              {run?.plan_name ? ` of ${run.plan_name}` : ""}
              {run?.plan_version ? ` (version ${run.plan_version})` : ""}
              {` · ${count} VM${count === 1 ? "" : "s"} · requested by ${run?.requested_by || "?"} `}
              <span title={formatDateTime(a.created_at)}>{timeAgo(a.created_at)}</span>
              {emergency ? <div>Started at once (pre-approved emergency): review what was done.</div> : null}
              {a.comment ? <div>{emergency ? a.comment : `Reason: ${a.comment}`}</div> : null}
            </div>
            {mine ? (
              <Muted>{emergency ? "Waiting for someone else to review" : "Waiting for someone else to decide"}</Muted>
            ) : emergency ? (
              <Controls>
                <OpsInput
                  value={comments[a.id] || ""}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setComments({ ...comments, [a.id]: e.target.value })}
                  placeholder="What you checked (required)"
                  style={{ width: 220 }}
                  data-test-id={`dr-approval-comment-${a.id}`}
                />
                <ActionButton
                  type="button"
                  disabled={busy === a.id || !comment}
                  onClick={() => decide(a, "review")}
                  data-test-id={`dr-approval-review-${a.id}`}
                >
                  <Check size={16} /> Review
                </ActionButton>
              </Controls>
            ) : (
              <Controls>
                <OpsInput
                  value={comments[a.id] || ""}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setComments({ ...comments, [a.id]: e.target.value })}
                  placeholder="Comment (optional)"
                  style={{ width: 200 }}
                  data-test-id={`dr-approval-comment-${a.id}`}
                />
                <ActionButton
                  type="button"
                  disabled={busy === a.id}
                  onClick={() => decide(a, "approve")}
                  data-test-id={`dr-approval-approve-${a.id}`}
                >
                  <Check size={16} /> Approve
                </ActionButton>
                <ActionButton
                  type="button"
                  variant="danger"
                  disabled={busy === a.id}
                  onClick={() => decide(a, "reject")}
                  data-test-id={`dr-approval-reject-${a.id}`}
                >
                  <X size={16} /> Reject
                </ActionButton>
              </Controls>
            )}
          </Row>
        );
      })}
    </Panel>
  );
};

export default DrApprovals;
